import React, { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { get } from '../../helpers/httpClient';

const FIELD_TYPES = ['text', 'email', 'phone', 'textarea', 'select', 'checkbox'];

function ToggleSwitch({ checked, onChange }) {
  return (
    <label className="toggle-switch">
      <input type="checkbox" checked={checked} onChange={onChange} />
      <span className="toggle-track" />
      <span className="toggle-thumb" />
    </label>
  );
}

const emptyField = {
  name: '',
  type: 'text',
  label: '',
  placeholder: '',
  required: false,
  options: [],
};

export default function InquiryFormWidgetEditor({ item, onChange, websiteId }) {
  const [activeTab, setActiveTab] = useState('fields');
  const [pendingOption, setPendingOption] = useState({});
  const [transports, setTransports] = useState([]);

  useEffect(() => {
    if (!websiteId) return;
    get(`websites/${websiteId}/transports`)
      .then((resp) => setTransports(resp?.transports ?? []))
      .catch(() => {});
  }, [websiteId]);

  const update = (field, val) => onChange?.({ ...item, [field]: val });

  const fields = item.fields || [];

  const addField = () => update('fields', [...fields, { ...emptyField }]);

  const removeField = (fIdx) => update('fields', fields.filter((_, i) => i !== fIdx));

  const moveField = (fIdx, dir) => {
    const arr = [...fields];
    const j = fIdx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[fIdx], arr[j]] = [arr[j], arr[fIdx]];
    update('fields', arr);
  };

  const updateField = (fIdx, key, val) =>
    update('fields', fields.map((f, i) => (i === fIdx ? { ...f, [key]: val } : f)));

  const commitOption = (fIdx) => {
    const val = (pendingOption[fIdx] || '').trim();
    if (!val) return;
    const f = fields[fIdx];
    updateField(fIdx, 'options', [...(f.options || []), val]);
    setPendingOption((p) => ({ ...p, [fIdx]: '' }));
  };

  const removeOption = (fIdx, oIdx) =>
    updateField(fIdx, 'options', (fields[fIdx].options || []).filter((_, i) => i !== oIdx));

  return (
    <div className="mt-3">
      <div className="profile-tabs mb-3">
        <div className="tabs-row">
          <button type="button" className={`tab-btn${activeTab === 'fields' ? ' active' : ''}`} onClick={() => setActiveTab('fields')}>
            Fields ({fields.length})
          </button>
          <button type="button" className={`tab-btn${activeTab === 'settings' ? ' active' : ''}`} onClick={() => setActiveTab('settings')}>
            Settings
          </button>
        </div>
      </div>

      {activeTab === 'fields' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
            <button type="button" className="primary-button btn btn-sm" onClick={addField}>+ Add field</button>
          </div>

          {fields.length === 0 && <p className="muted-hint mb-0">No fields added yet</p>}

          {fields.map((f, fIdx) => (
            <div key={fIdx} className="inq-field-row">
              <div className="inq-field-main">
                <Form.Group style={{ width: 96, flexShrink: 0 }}>
                  <Form.Label className="component-item-field-label">Type</Form.Label>
                  <Form.Select size="sm" value={f.type} onChange={(e) => updateField(fIdx, 'type', e.target.value)}>
                    {FIELD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </Form.Select>
                </Form.Group>

                <Form.Group style={{ flex: 1, minWidth: 90 }}>
                  <Form.Label className="component-item-field-label">Name</Form.Label>
                  <Form.Control size="sm" value={f.name} onChange={(e) => updateField(fIdx, 'name', e.target.value)} placeholder="e.g. email" />
                </Form.Group>

                <Form.Group style={{ flex: 1.4, minWidth: 110 }}>
                  <Form.Label className="component-item-field-label">Label</Form.Label>
                  <Form.Control size="sm" value={f.label} onChange={(e) => updateField(fIdx, 'label', e.target.value)} placeholder="Displayed label" />
                </Form.Group>

                {f.type !== 'checkbox' && f.type !== 'select' && (
                  <Form.Group style={{ flex: 1.4, minWidth: 110 }}>
                    <Form.Label className="component-item-field-label">Placeholder</Form.Label>
                    <Form.Control size="sm" value={f.placeholder} onChange={(e) => updateField(fIdx, 'placeholder', e.target.value)} placeholder="Hint text" />
                  </Form.Group>
                )}

                <div className="inq-field-toggles">
                  <div className="comp-setting-row" style={{ gap: 6 }}>
                    <span className="component-item-field-label">Required</span>
                    <ToggleSwitch checked={f.required} onChange={(e) => updateField(fIdx, 'required', e.target.checked)} />
                  </div>

                </div>

                <div className="inq-field-actions">
                  <button type="button" className="icon-btn" onClick={() => moveField(fIdx, -1)} disabled={fIdx === 0} title="Move up">↑</button>
                  <button type="button" className="icon-btn" onClick={() => moveField(fIdx, 1)} disabled={fIdx === fields.length - 1} title="Move down">↓</button>
                  <button type="button" className="structure-delete-btn" onClick={() => removeField(fIdx)} title="Remove">×</button>
                </div>
              </div>

              {f.type === 'select' && (
                <div className="inq-options">
                  <span className="component-item-field-label">Options</span>
                  <div className="inq-options-tags">
                    {(f.options || []).map((opt, oIdx) => (
                      <span key={oIdx} className="tag-chip">
                        {opt}
                        <button type="button" className="tag-chip-remove" onClick={() => removeOption(fIdx, oIdx)}>×</button>
                      </span>
                    ))}
                  </div>
                  <div className="inq-options-add">
                    <Form.Control
                      size="sm"
                      value={pendingOption[fIdx] || ''}
                      onChange={(e) => setPendingOption((p) => ({ ...p, [fIdx]: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitOption(fIdx); } }}
                      placeholder="Add option…"
                      style={{ maxWidth: 200 }}
                    />
                    <button type="button" className="primary-button btn btn-sm" onClick={() => commitOption(fIdx)}>Add</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {activeTab === 'settings' && (
        <div className="comp-settings">
          <Form.Group className="mb-3">
            <Form.Label className="component-item-field-label">Form title</Form.Label>
            <Form.Control size="sm" value={item.title || ''} onChange={(e) => update('title', e.target.value)} placeholder="e.g. Contact us" />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="component-item-field-label">Description</Form.Label>
            <Form.Control as="textarea" rows={2} size="sm" value={item.description || ''} onChange={(e) => update('description', e.target.value)} placeholder="Optional subtitle" />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="component-item-field-label">Submit button label</Form.Label>
            <Form.Control size="sm" value={item.submitLabel || ''} onChange={(e) => update('submitLabel', e.target.value)} placeholder="Send" />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="component-item-field-label">Success message</Form.Label>
            <Form.Control as="textarea" rows={2} size="sm" value={item.successMessage || ''} onChange={(e) => update('successMessage', e.target.value)} placeholder="Shown after successful submission" />
          </Form.Group>
          <hr className="my-3" />
          <Form.Group className="mb-3">
            <Form.Label className="component-item-field-label">Transport</Form.Label>
            <Form.Select
              size="sm"
              value={item.transportId || ''}
              onChange={(e) => update('transportId', e.target.value || null)}
              disabled={transports.length === 0}
            >
              <option value="">{transports.length === 0 ? 'Loading…' : '— select —'}</option>
              {transports.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </div>
      )}
    </div>
  );
}
